import React, { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLookup } from "@/components/shared";
import { X, Plus, Trash, CalendarX } from "@phosphor-icons/react";
import { QRCodeCanvas } from "qrcode.react";

const emptyForecast = () => ({ asset_type: "", hire_type: "Operated", qty: 1, notes: "" });
const AU_REGIONS = ["AU-ACT", "AU-NSW", "AU-NT", "AU-QLD", "AU-SA", "AU-TAS", "AU-VIC", "AU-WA"];
const defaultAutoSd = () => ({ saturday: false, sunday: false, region: "", public_holidays: [] });

function _fmtT(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
}

function AttCol({ title, color, rows, empty, testid }) {
  return (
    <div className="border border-gray-200" data-testid={testid}>
      <div className={`px-3 py-1.5 bg-gray-50 border-b border-gray-200 text-[11px] font-bold uppercase tracking-wider ${color}`}>{title}</div>
      <div className="divide-y divide-gray-100">
        {rows.length === 0 ? (
          <p className="text-xs text-gray-400 px-3 py-3">{empty}</p>
        ) : rows.map((r, i) => (
          <div key={i} className="px-3 py-2 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-equipr-black truncate">{r.label}</div>
              {r.sub && <div className="text-[11px] text-gray-400 truncate">{r.sub}</div>}
            </div>
            {r.time && <div className="text-[11px] text-gray-500 shrink-0">{_fmtT(r.time)}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ProjectDetailsDialog({ open, onClose, onSaved, project }) {
  const hireTypes = useLookup("hire_types");
  const assetTypes = useLookup("asset_types");
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState(null);
  const [fLine, setFLine] = useState(emptyForecast());
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("details");
  const [hYear, setHYear] = useState(new Date().getFullYear());
  const [hNew, setHNew] = useState({ date: "", name: "" });
  const [tNew, setTNew] = useState({ code: "", name: "", description: "" });
  const [sdBusy, setSdBusy] = useState(false);
  const [sdMsg, setSdMsg] = useState("");

  useEffect(() => {
    if (open) {
      api.get("/customers").then(({ data }) => setCustomers(data.map((c) => c.name))).catch(() => {});
      setForm(project ? {
        project_name: "", project_no: "", primary_contact: "", customer_po: "",
        project_start: "", project_finish: "", office_notes: "", client: "", site_address: "",
        ...project, forecast: project.forecast || [], site_contacts: project.site_contacts || [],
        auto_standdown: project.auto_standdown || defaultAutoSd(),
      } : {
        client: "", project_name: "", project_no: "", site_address: "", primary_contact: "",
        site_contacts: [], customer_po: "", project_start: "", project_finish: "", days: 0, status: "active",
        office_notes: "", forecast: [], equipment: [], assignments: [], auto_standdown: defaultAutoSd(),
      });
      setError("");
      setFLine(emptyForecast());
      setTab("details");
      setSdMsg("");
    }
  }, [open, project]);

  if (!open || !form) return null;

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setF = (k, v) => setFLine((s) => ({ ...s, [k]: v }));

  const addForecast = () => {
    if (!fLine.asset_type) return;
    setForm((s) => ({ ...s, forecast: [...(s.forecast || []), { ...fLine, id: crypto.randomUUID() }] }));
    setFLine(emptyForecast());
  };
  const removeForecast = (idx) => setForm((s) => ({ ...s, forecast: s.forecast.filter((_, i) => i !== idx) }));

  const addTask = () => {
    if (!tNew.name.trim()) return;
    setForm((s) => ({ ...s, tasks: [...(s.tasks || []), { ...tNew, id: crypto.randomUUID(), active: true }] }));
    setTNew({ code: "", name: "", description: "" });
  };
  const toggleTask = (i) => setForm((s) => { const arr = [...(s.tasks || [])]; arr[i] = { ...arr[i], active: arr[i].active === false }; return { ...s, tasks: arr }; });
  const removeTask = (i) => setForm((s) => ({ ...s, tasks: (s.tasks || []).filter((_, j) => j !== i) }));

  const computedDays = (() => {
    const s = form.project_start ? new Date(form.project_start) : null;
    const e = form.project_finish ? new Date(form.project_finish) : null;
    if (s && e && e >= s) return Math.round((e - s) / 86400000) + 1;
    return 0;
  })();
  const addContact = () => setForm((s) => ({ ...s, site_contacts: [...(s.site_contacts || []), { name: "", mobile: "", email: "" }] }));
  const setContact = (i, k, v) => setForm((s) => { const arr = [...(s.site_contacts || [])]; arr[i] = { ...arr[i], [k]: v }; return { ...s, site_contacts: arr }; });
  const removeContact = (i) => setForm((s) => ({ ...s, site_contacts: (s.site_contacts || []).filter((_, j) => j !== i) }));

  const sd = form?.auto_standdown || defaultAutoSd();
  const setSd = (patch) => setForm((s) => ({ ...s, auto_standdown: { ...(s.auto_standdown || defaultAutoSd()), ...patch } }));
  const addHoliday = () => {
    if (!hNew.date) return;
    const list = sd.public_holidays || [];
    if (list.some((h) => h.date === hNew.date)) { setHNew({ date: "", name: "" }); return; }
    setSd({ public_holidays: [...list, { date: hNew.date, name: hNew.name || "Public Holiday" }].sort((a, b) => a.date.localeCompare(b.date)) });
    setHNew({ date: "", name: "" });
  };
  const removeHoliday = (d) => setSd({ public_holidays: (sd.public_holidays || []).filter((h) => h.date !== d) });
  const loadRegionHolidays = async () => {
    if (!sd.region) { setError("Pick a region first."); return; }
    setSdBusy(true); setError(""); setSdMsg("");
    try {
      const { data } = await api.get("/public-holidays", { params: { year: hYear, region: sd.region } });
      const existing = new Set((sd.public_holidays || []).map((h) => h.date));
      const merged = [...(sd.public_holidays || []), ...data.filter((h) => !existing.has(h.date))].sort((a, b) => a.date.localeCompare(b.date));
      setSd({ public_holidays: merged });
      setSdMsg(`Loaded ${data.length} holidays for ${sd.region} ${hYear}.`);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    setSdBusy(false);
  };
  const applyAutoStandDown = async () => {
    if (!project?.id) { setError("Save the project first, then apply auto stand-downs."); return; }
    setSdBusy(true); setError(""); setSdMsg("");
    try {
      await api.put(`/projects/${project.id}`, { ...form, days: Number(form.days) || 0 });
      const { data } = await api.post(`/projects/${project.id}/auto-standdown`, sd);
      setSdMsg(`Applied — ${data.created} stand-down range(s) created across project assets.`);
      onSaved();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    setSdBusy(false);
  };

  const save = async () => {
    setError("");
    if (!form.client || !form.site_address) { setError("Client and Site Address / Location are required."); return; }
    setSaving(true);
    try {
      const payload = { ...form, days: Number(form.days) || 0 };
      if (project?.id) await api.put(`/projects/${project.id}`, payload);
      else await api.post("/projects", payload);
      onSaved();
      onClose();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const inp = "w-full h-9 px-2 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none";
  const lbl = "text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted mb-1.5 block";

  return (
    <div className="fixed inset-0 z-50 bg-[rgba(26,26,26,0.34)] flex justify-end eq-sheet-overlay" onClick={onClose}>
      <div className="bg-surface-panel shadow-drawer border-l border-border-warm w-full max-w-[800px] h-full flex flex-col eq-sheet" onClick={(e) => e.stopPropagation()} data-testid="project-details-dialog">
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-divider-inner shrink-0">
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted">{project ? "Edit Project" : "New Project"}</div>
            <h3 className="font-bold text-[20px] text-ink-primary leading-tight mt-0.5">{project ? (form.project_name || form.site_address || "Project") : "New Project"}</h3>
            {project && (form.project_no || form.client) && <div className="text-[12px] text-ink-tertiary font-mono mt-0.5">{[form.project_no, form.client].filter(Boolean).join(" · ")}</div>}
          </div>
          <button onClick={onClose} data-testid="project-details-close" className="w-9 h-9 flex items-center justify-center rounded-[9px] border border-border-control text-ink-secondary hover:bg-surface-panel"><X size={16} weight="bold" /></button>
        </div>

        <div className="flex border-b border-divider-inner px-3 shrink-0 overflow-x-auto">
          {[["details", "Details"], ["contacts", "Site Contacts"], ["tasks", "Tasks"], ["forecast", "Forecast"], ["standdowns", "Stand Downs"], ["attendance", "Attendance"], ["sitediary", "Site Diary"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`pd-tab-${k}`}
              className={`px-4 py-2 text-sm border-b-2 whitespace-nowrap ${tab === k ? "border-equipr-yellow text-ink-primary font-bold" : "border-transparent text-ink-tertiary hover:text-ink-primary"}`}>{l}</button>
          ))}
        </div>

        {tab === "details" && (
        <div className="flex-1 overflow-auto p-5 space-y-5">
          {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="project-details-error">{error}</div>}

          {/* Project details */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><label className={lbl}>Project Name</label>
              <input className={inp} value={form.project_name} onChange={(e) => set("project_name", e.target.value)} placeholder="e.g. Mount Cotton Earthworks" data-testid="pd-name" /></div>
            <div><label className={lbl}>Project Number</label>
              <input className={inp} value={form.project_no} onChange={(e) => set("project_no", e.target.value)} placeholder="auto" data-testid="pd-number" /></div>

            <div><label className={lbl}>Client *</label>
              <input list="pd-cust" className={inp} value={form.client} onChange={(e) => set("client", e.target.value)} data-testid="pd-client" />
              <datalist id="pd-cust">{customers.map((c) => <option key={c} value={c} />)}</datalist></div>
            <div><label className={lbl}>Primary Contact</label><input className={inp} value={form.primary_contact} onChange={(e) => set("primary_contact", e.target.value)} data-testid="pd-primary-contact" /></div>
            <div><label className={lbl}>Customer PO#</label><input className={inp} value={form.customer_po} onChange={(e) => set("customer_po", e.target.value)} /></div>

            <div className="col-span-3"><label className={lbl}>Site Address / Location *</label>
              <input className={inp} value={form.site_address} onChange={(e) => set("site_address", e.target.value)} data-testid="pd-location" /></div>

            <div><label className={lbl}>Project Start</label><input type="date" className={inp} value={form.project_start || ""} onChange={(e) => set("project_start", e.target.value)} data-testid="pd-project-start" /></div>
            <div><label className={lbl}>Project Finish</label><input type="date" className={inp} value={form.project_finish || ""} onChange={(e) => set("project_finish", e.target.value)} data-testid="pd-project-finish" /></div>
            <div><label className={lbl}>Duration</label>
              <div className="h-9 px-2 border border-gray-200 bg-gray-50 text-sm flex items-center text-gray-600" data-testid="pd-days">{computedDays ? `${computedDays} day${computedDays === 1 ? "" : "s"}` : "—"}</div></div>

            <div><label className={lbl}>Status</label>
              <select className={inp} value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="pd-status">
                <option value="active">Active</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
              </select></div>
          </div>

          {/* Office notes */}
          <div><label className={lbl}>Office Notes</label>
            <textarea className={inp.replace("h-9", "h-64") + " py-2 leading-relaxed resize-y"} value={form.office_notes} onChange={(e) => set("office_notes", e.target.value)} data-testid="pd-office-notes" placeholder="Internal notes, access details, special instructions…" /></div>
        </div>
        )}
        {tab === "contacts" && (
        <div className="flex-1 overflow-auto p-5" data-testid="pd-contacts-tab">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-500">Site contacts for this project — add as many as needed.</p>
            <button onClick={addContact} data-testid="pd-contact-add" className="h-8 px-3 bg-equipr-yellow text-equipr-black text-sm font-semibold rounded-none hover:bg-equipr-yellow-dark inline-flex items-center gap-1"><Plus size={14} weight="bold" /> Add contact</button>
          </div>
          {(form.site_contacts || []).length === 0 ? (
            <div className="border border-dashed border-gray-300 py-10 text-center text-sm text-gray-400">No site contacts yet.</div>
          ) : (
            <table className="w-full text-cell">
              <thead><tr className="text-left text-gray-500 border-b border-gray-200">
                {["Name", "Mobile", "Email", ""].map((h) => <th key={h} className="px-2 py-1.5 font-semibold uppercase tracking-wider text-[11px]">{h}</th>)}
              </tr></thead>
              <tbody>
                {(form.site_contacts || []).map((c, i) => (
                  <tr key={i} className="border-b border-gray-50" data-testid={`pd-contact-row-${i}`}>
                    <td className="px-1.5 py-1"><input className={inp} value={c.name || ""} onChange={(e) => setContact(i, "name", e.target.value)} data-testid={`pd-contact-name-${i}`} /></td>
                    <td className="px-1.5 py-1"><input className={inp} value={c.mobile || ""} onChange={(e) => setContact(i, "mobile", e.target.value)} data-testid={`pd-contact-mobile-${i}`} /></td>
                    <td className="px-1.5 py-1"><input className={inp} type="email" value={c.email || ""} onChange={(e) => setContact(i, "email", e.target.value)} data-testid={`pd-contact-email-${i}`} /></td>
                    <td className="px-1.5 py-1 text-right"><button onClick={() => removeContact(i)} className="text-gray-400 hover:text-red-600" data-testid={`pd-contact-remove-${i}`}><Trash size={15} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        )}
        {tab === "tasks" && (
        <div className="flex-1 overflow-auto p-5 space-y-4" data-testid="pd-tasks-tab">
          <p className="text-xs text-gray-500">Tasks / Activities workers can record against dockets on this project. Deactivated tasks stay on old dockets but are hidden from new ones.</p>
          <div className="border border-gray-200">
            <div className="bg-gray-100 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-gray-600">Add Task / Activity</div>
            <div className="p-3 grid grid-cols-12 gap-2 items-end">
              <div className="col-span-2"><label className={lbl}>Code</label>
                <input className={inp} value={tNew.code} onChange={(e) => setTNew((s) => ({ ...s, code: e.target.value }))} placeholder="e.g. 1.02" data-testid="task-code-input" /></div>
              <div className="col-span-4"><label className={lbl}>Name *</label>
                <input className={inp} value={tNew.name} onChange={(e) => setTNew((s) => ({ ...s, name: e.target.value }))} placeholder="e.g. Bulk Earthworks" data-testid="task-name-input"
                  onKeyDown={(e) => e.key === "Enter" && addTask()} /></div>
              <div className="col-span-5"><label className={lbl}>Description</label>
                <input className={inp} value={tNew.description} onChange={(e) => setTNew((s) => ({ ...s, description: e.target.value }))} data-testid="task-desc-input" /></div>
              <div className="col-span-1">
                <button onClick={addTask} disabled={!tNew.name.trim()} data-testid="task-add-btn"
                  className="h-9 w-full bg-equipr-yellow text-equipr-black rounded-none hover:bg-equipr-yellow-dark disabled:opacity-50 flex items-center justify-center"><Plus size={16} weight="bold" /></button>
              </div>
            </div>
          </div>
          <div className="border border-gray-200">
            <table className="w-full text-cell">
              <thead><tr className="text-left text-gray-500 bg-gray-50">
                {["Code", "Name", "Description", "Status", ""].map((h) => <th key={h} className="px-3 py-1.5 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody>
                {(form.tasks || []).length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-4 text-center text-gray-400" data-testid="tasks-empty">No tasks yet — add the activities workers will book time against.</td></tr>
                ) : form.tasks.map((t, i) => (
                  <tr key={t.id || i} className={`border-t border-gray-100 ${t.active === false ? "opacity-50" : ""}`} data-testid={`task-row-${i}`}>
                    <td className="px-3 py-1.5 font-mono">{t.code || "—"}</td>
                    <td className="px-3 py-1.5 font-semibold">{t.name}</td>
                    <td className="px-3 py-1.5 text-gray-500">{t.description || "—"}</td>
                    <td className="px-3 py-1.5">
                      <button onClick={() => toggleTask(i)} data-testid={`task-toggle-${i}`}
                        className={`px-2 py-0.5 text-[11px] font-bold rounded-sm ${t.active === false ? "bg-gray-200 text-gray-600" : "bg-green-100 text-green-700"}`}>
                        {t.active === false ? "INACTIVE" : "ACTIVE"}</button>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <button onClick={() => removeTask(i)} data-testid={`task-delete-${i}`} className="text-gray-400 hover:text-red-600"><Trash size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-gray-400">Remember to Confirm below to save task changes.</p>
        </div>
        )}

        {tab === "forecast" && (
        <div className="flex-1 overflow-auto p-5" data-testid="pd-forecast-tab">
          <div className="border border-gray-200">
            <div className="bg-gray-100 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-gray-600">Forecast — Equipment / Workers Needed</div>
            <div className="p-3 grid grid-cols-12 gap-2 items-end">
              <div className="col-span-4"><label className={lbl}>Asset Type / Role</label>
                <select className={inp} value={fLine.asset_type} onChange={(e) => setF("asset_type", e.target.value)} data-testid="forecast-asset-type">
                  <option value="">— Select —</option>{assetTypes.map((a) => <option key={a}>{a}</option>)}
                </select></div>
              <div className="col-span-3"><label className={lbl}>Hire Type</label>
                <select className={inp} value={fLine.hire_type} onChange={(e) => setF("hire_type", e.target.value)} data-testid="forecast-hire-type">
                  {hireTypes.map((h) => <option key={h}>{h}</option>)}
                </select></div>
              <div className="col-span-1"><label className={lbl}>Qty</label>
                <input type="number" className={inp} value={fLine.qty} onChange={(e) => setF("qty", e.target.value)} /></div>
              <div className="col-span-3"><label className={lbl}>Notes</label>
                <input className={inp} value={fLine.notes} onChange={(e) => setF("notes", e.target.value)} /></div>
              <div className="col-span-1">
                <button onClick={addForecast} disabled={!fLine.asset_type} data-testid="forecast-add" className="h-9 w-full bg-equipr-yellow text-equipr-black rounded-none hover:bg-equipr-yellow-dark disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"><Plus size={16} weight="bold" /></button>
              </div>
            </div>
            <table className="w-full text-[12px]">
              <thead><tr className="text-left text-gray-500 border-t border-gray-100">
                {["Asset Type / Role", "Hire Type", "Qty", "Notes", ""].map((h) => <th key={h} className="px-3 py-1.5 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody>
                {(form.forecast || []).length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-4 text-center text-gray-400">No forecast items yet.</td></tr>
                ) : form.forecast.map((f, i) => (
                  <tr key={f.id || i} className="border-t border-gray-100" data-testid={`forecast-row-${i}`}>
                    <td className="px-3 py-1.5 font-semibold">{f.asset_type}</td>
                    <td className="px-3 py-1.5">{f.hire_type}</td>
                    <td className="px-3 py-1.5">{f.qty}</td>
                    <td className="px-3 py-1.5">{f.notes || "—"}</td>
                    <td className="px-3 py-1.5 text-right"><button onClick={() => removeForecast(i)} className="text-gray-400 hover:text-red-600"><Trash size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}
        {tab === "standdowns" && (
        <div className="flex-1 overflow-auto p-5 space-y-5" data-testid="pd-standdowns-tab">
          {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="project-details-error">{error}</div>}
          {sdMsg && <div className="border border-green-300 bg-green-50 text-green-700 text-sm px-3 py-2" data-testid="pd-sd-msg">{sdMsg}</div>}

          <div className="border border-gray-200">
            <div className="bg-gray-100 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-gray-600">Recurring — Weekends</div>
            <div className="p-4 flex gap-6">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!sd.saturday} onChange={(e) => setSd({ saturday: e.target.checked })} data-testid="pd-sd-saturday" /> Stand down Saturdays</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!sd.sunday} onChange={(e) => setSd({ sunday: e.target.checked })} data-testid="pd-sd-sunday" /> Stand down Sundays</label>
            </div>
          </div>

          <div className="border border-gray-200">
            <div className="bg-gray-100 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-gray-600">Public Holidays & Shutdowns</div>
            <div className="p-4 space-y-3">
              <div className="flex items-end gap-2 flex-wrap">
                <div><label className={lbl}>Region</label>
                  <select className={inp + " w-36"} value={sd.region || ""} onChange={(e) => setSd({ region: e.target.value })} data-testid="pd-sd-region">
                    <option value="">— Select —</option>{AU_REGIONS.map((r) => <option key={r}>{r}</option>)}
                  </select></div>
                <div><label className={lbl}>Year</label>
                  <select className={inp + " w-24"} value={hYear} onChange={(e) => setHYear(Number(e.target.value))} data-testid="pd-sd-year">
                    {[0, 1, 2].map((n) => { const y = new Date().getFullYear() + n; return <option key={y} value={y}>{y}</option>; })}
                  </select></div>
                <button onClick={loadRegionHolidays} disabled={sdBusy} data-testid="pd-sd-load"
                  className="h-9 px-3 border border-border-control bg-white text-equipr-black text-[12px] uppercase tracking-[0.04em] font-semibold rounded-[7px] hover:bg-surface-panel active:bg-surface-pressed disabled:opacity-50">Load holidays</button>
              </div>

              <div className="flex items-end gap-2">
                <div><label className={lbl}>Add date</label><input type="date" className={inp + " w-40"} value={hNew.date} onChange={(e) => setHNew((s) => ({ ...s, date: e.target.value }))} data-testid="pd-sd-holiday-date" /></div>
                <div className="flex-1"><label className={lbl}>Name / reason (e.g. severe weather)</label><input className={inp} value={hNew.name} onChange={(e) => setHNew((s) => ({ ...s, name: e.target.value }))} data-testid="pd-sd-holiday-name" /></div>
                <button onClick={addHoliday} data-testid="pd-sd-holiday-add" className="h-9 w-9 bg-equipr-yellow text-equipr-black rounded-none hover:bg-equipr-yellow-dark flex items-center justify-center"><Plus size={16} weight="bold" /></button>
              </div>

              <table className="w-full text-[12px]">
                <thead><tr className="text-left text-gray-500 border-b border-gray-100">{["Date", "Name", ""].map((h) => <th key={h} className="px-2 py-1.5 font-semibold">{h}</th>)}</tr></thead>
                <tbody>
                  {(sd.public_holidays || []).length === 0 ? (
                    <tr><td colSpan={3} className="px-2 py-4 text-center text-gray-400">No public holidays / shutdown dates yet.</td></tr>
                  ) : (sd.public_holidays || []).map((h) => (
                    <tr key={h.date} className="border-b border-gray-50" data-testid={`pd-sd-holiday-${h.date}`}>
                      <td className="px-2 py-1.5 font-semibold">{h.date}</td>
                      <td className="px-2 py-1.5">{h.name}</td>
                      <td className="px-2 py-1.5 text-right"><button onClick={() => removeHoliday(h.date)} className="text-gray-400 hover:text-red-600"><Trash size={14} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="border border-equipr-yellow bg-equipr-yellow/10 p-4 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-gray-600 max-w-md">
              <b className="text-equipr-black inline-flex items-center gap-1"><CalendarX size={14} /> Apply</b> creates Stand Down records for <b>all assets on this project</b> on the selected weekends & holidays (within each asset's hire dates). Re-applying replaces previously auto-generated ranges.
            </p>
            <button onClick={applyAutoStandDown} disabled={sdBusy} data-testid="pd-sd-apply"
              className="h-9 px-5 bg-equipr-black text-white text-sm font-bold rounded-none hover:bg-gray-800 disabled:opacity-50">
              {sdBusy ? "Applying…" : "Apply auto stand-downs"}
            </button>
          </div>
        </div>
        )}
        {tab === "sitediary" && (
        <div className="flex-1 overflow-auto p-5" data-testid="pd-sitediary-tab">
          {!project?.id ? (
            <div className="text-sm text-gray-500 py-10 text-center">Save the project first to generate its Site Diary QR code and PIN.</div>
          ) : (
            <div className="text-center py-2" data-testid="pd-sitediary-qr">
              <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">On-site supervisors scan this QR (or open the link) and enter the Site PIN to complete the daily site diary. Print it and keep it in the site office.</p>
              <div className="flex justify-center">
                <QRCodeCanvas value={`${window.location.origin}/site-diary?project=${project.id}`} size={210} level="M" includeMargin data-testid="pd-sitediary-canvas" />
              </div>
              <div className="mt-4 inline-block border border-gray-200 px-6 py-3">
                <div className="text-[11px] uppercase tracking-wider text-gray-400">Site PIN</div>
                <div className="text-3xl font-extrabold tracking-[0.35em] text-equipr-black" data-testid="pd-sitediary-pin">{form.site_diary_pin || project.site_diary_pin || "----"}</div>
              </div>
              <p className="text-[11px] text-gray-400 mt-3 break-all">{`${window.location.origin}/site-diary?project=${project.id}`}</p>
            </div>
          )}
        </div>
        )}

        {tab === "attendance" && (
        <div className="flex-1 overflow-auto p-5 space-y-5" data-testid="pd-attendance-tab">
          {!project?.id ? (
            <div className="text-sm text-gray-500 py-10 text-center">Save the project first to generate its Worker Sign-In QR code and PIN.</div>
          ) : (
            <>
              <div className="text-center border border-gray-200 p-4">
                <p className="text-sm text-gray-500 mb-3 max-w-md mx-auto">Print & post at the site entry. Workers scan it, enter the Site PIN, then their personal Worker PIN to sign in / out.</p>
                <div className="flex justify-center">
                  <QRCodeCanvas value={`${window.location.origin}/sign-in?project=${project.id}`} size={180} level="M" includeMargin data-testid="pd-signin-canvas" />
                </div>
                <div className="mt-3 inline-block border border-gray-200 px-6 py-2">
                  <div className="text-[11px] uppercase tracking-wider text-gray-400">Site PIN</div>
                  <div className="text-3xl font-extrabold tracking-[0.35em] text-equipr-black" data-testid="pd-signin-pin">{form.sign_in_pin || project.sign_in_pin || "----"}</div>
                </div>
                <p className="text-[11px] text-gray-400 mt-2 break-all">{`${window.location.origin}/sign-in?project=${project.id}`}</p>
              </div>
              <p className="text-xs text-gray-500 text-center">Live sign-in status (who's on site / missing) is shown on the <b>Scheduler → On Site</b> tab.</p>
            </>
          )}
        </div>
        )}

        <div className="flex justify-between items-center gap-2 px-5 py-3 bg-surface-panel border-t border-divider-inner shrink-0">
          <p className="text-[11px] text-gray-400">Allocate actual assets & operators by dragging them onto the project from the right rail.</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="h-9 px-4 border border-border-control bg-white text-equipr-black text-[12px] uppercase tracking-[0.04em] font-semibold rounded-[7px] hover:bg-surface-panel active:bg-surface-pressed">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="project-details-save" className="h-9 px-5 bg-equipr-yellow text-equipr-black text-[12px] uppercase tracking-[0.04em] font-bold rounded-[7px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px disabled:opacity-60">
              {saving ? "Saving…" : "Save Project"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
